// @ts-check
import { gql } from '@apollo/client/core';

/** @type {import('../../components/Preview').BeforeRenderEvent} */
export default async function LKBurgenlandEinreichung_beforeRender({
  client,
  collectionId,
  companyId,
}) {
  try {
    const { data } = await client.query({
      query: checkEinreichnummerQuery,
      variables: { collectionId, companyId },
    });
    const { renderedRegistrations, lastRegistrationNumber } = afterQuery(data);
    //Update all registrations needing a registrationNumber.
    await Promise.all(
      renderedRegistrations.map(async (registration, index) => {
        const newRegistrationNumber = await setRegistrationNumber(
          client,
          registration,
          lastRegistrationNumber + index + 1 // TODO using the index isn't completely correct - we'd have to increment a counter or the lastRegistrationNumber.
        );

        // Then set the productNumber for all registeredProducts without a productNumber.
        const lastProductNumber = Number(registration.lastProductNumber || 0);

        const updated = await Promise.all(
          registration.registeredProducts.nodes
            .filter(
              (product) => !product.productNumber || !product.einreichnummer
            )
            .map((registeredProduct, index) =>
              setProductNumber(
                client,
                registeredProduct,
                lastProductNumber + index + 1,
                `${String(newRegistrationNumber).padStart(3, '0')}-${String(
                  lastProductNumber + index + 1
                ).padStart(2, '0')}`
              )
            )
        );
        console.log({ collectionId, companyId, lastProductNumber });
        if (!updated.length) return;
        await setLastProductNumber(
          client,
          registration,
          lastProductNumber + updated.length
        );
      })
    );
  } catch (error) {
    // Don't fail on errors, just log them. Otherwise we wouldn't render the PDF.
    // We risk that we don't get an automatic "Einreichnummer" value, but that's better than not rendering the PDF at all.
    console.error(error);
  }
}

/**
 * @type {import('@apollo/client').TypedDocumentNode<
 * import('./@types/LKEinreichung_beforeRender').LkBurgenlandEinreichung_CheckEinreichnummerQuery,
 * import('./@types/LKEinreichung_beforeRender').LkBurgenlandEinreichung_CheckEinreichnummerQueryVariables>
 * }
 */
const checkEinreichnummerQuery = gql`
  query LKBurgenlandEinreichung_CheckEinreichnummer(
    $collectionId: ID!
    # For some strange reason, filtering on an undefined registrationId on the server yields no result for renderedRegistrations, but in Apollo Client it does.
    # $registrationId: ID
    $companyId: ID
  ) {
    version
    # By using event here, I can avoid checking every registration's __typename for "SingleRegistration"
    # - events only have single registrations,
    # whereas collections could be eventGroups, which have GroupedRegistrations.
    collection: collection(collectionId: $collectionId) {
      __typename
      ... on Event {
        # We need to check the full list for the highest registrationNumber.
        allRegistrations: registrations {
          nodes {
            registrationNumber: customFieldText(key: "registrationNumber")
            stand {
              name
            }
            registrationId # unique identifier for Apollo Client
          }
        }
        renderedRegistrations: registrations(
          filter: {
            companyId: { eq: $companyId }
            # registrationId: { eq: $registrationId }
          }
        ) {
          nodes {
            registrationId # unique identifier for Apollo Client
            ... on SingleRegistration {
              stand {
                name
              }
              registrationNumber: customFieldText(key: "registrationNumber")
              lastProductNumber: customFieldText(key: "productNumber")
              registeredProducts {
                nodes {
                  registrationId
                  productId
                  productNumber: customFieldText(key: "productNumber")
                  einreichnummer: customFieldText(key: "einreichnummer")
                }
              }
            }
          }
        }
        ... on Event {
          eventId
        }
      }
    }
  }
`;

/**
 *
 * @param {Parameters<LKBurgenlandEinreichung_beforeRender>[0]["client"]} client
 * @param {ReturnType<afterQuery>["renderedRegistrations"][number]} registration
 * @param {number} newRegistrationNumber
 * @returns
 */
async function setRegistrationNumber(
  client,
  { registrationId, registrationNumber },
  newRegistrationNumber
) {
  if (registrationNumber) return registrationNumber;
  const text = `${newRegistrationNumber}`;
  const { data, errors } = await client.mutate({
    mutation: setRegistrationNumberMutation,
    variables: {
      input: {
        registrationId,
        key: 'registrationNumber',
        value: { text },
      },
    },
  });
  console.log(data, errors);
  if (!data?.result.success) throw new Error(data?.result.message);
  return text;
  // TODO return the actual result
  // return result.registration?.registrationNumber;
}

/** @type {import('@apollo/client').TypedDocumentNode<import('./@types/LKEinreichung_beforeRender').SetRegistrationNumberMutation, import('./@types/LKEinreichung_beforeRender').SetRegistrationNumberMutationVariables>} */
const setRegistrationNumberMutation = gql`
  mutation SetRegistrationNumber($input: SetRegistrationCustomFieldInput!) {
    result: setRegistrationCustomField(input: $input) {
      code
      success
      message
      registration(locale: de) {
        registrationId
        registrationNumber: customFieldText(key: "registrationNumber")
        productNumber: customFieldText(key: "productNumber")
      }
    }
  }
`;
/** @param {import('./@types/LKEinreichung_beforeRender').LkBurgenlandEinreichung_CheckEinreichnummerQuery} data */
function afterQuery(data) {
  if (data.collection?.__typename !== 'Event') return {};
  const allRegistrations = data.collection?.allRegistrations.nodes || [];
  const renderedRegistrations =
    data.collection?.renderedRegistrations.nodes || [];
  const lastRegistrationNumber = allRegistrations
    .map(({ registrationNumber }) =>
      registrationNumber ? Number(registrationNumber) : 0
    )
    .reduce((max, last) => Math.max(max, last), 0);
  return { allRegistrations, renderedRegistrations, lastRegistrationNumber };
}

/**
 *
 * @param {Parameters<LKBurgenlandEinreichung_beforeRender>[0]["client"]} client
 * @param {ReturnType<afterQuery>["renderedRegistrations"][number]["registeredProducts"]["nodes"][number]} registeredProduct
 * @param {number} newProductNumber
 * @param {string} newEinreichnummer
 * @returns
 */
async function setProductNumber(
  client,
  { registrationId, productId, productNumber },
  newProductNumber,
  newEinreichnummer
) {
  console.log(newEinreichnummer);
  const text = `${newProductNumber}`;
  const {
    data: { result },
    // errors,
  } = await client.mutate({
    mutation: setProductNumberMutation,
    variables: {
      input: {
        registrationId,
        productId,
        key: 'productNumber',
        value: { text },
      },
      einreichInput: {
        registrationId,
        productId,
        key: 'einreichnummer',
        value: { text: newEinreichnummer },
      },
    },
  });
  console.log(result);
  if (!result.success) throw new Error(result.message);
  return text;
}

/** @type {import('@apollo/client').TypedDocumentNode<import('./@types/LKEinreichung_beforeRender').SetProductNumberMutation, import('./@types/LKEinreichung_beforeRender').SetProductNumberMutationVariables>} */
const setProductNumberMutation = gql`
  mutation SetProductNumber(
    $input: SetRegisteredProductCustomFieldInput!
    $einreichInput: SetRegisteredProductCustomFieldInput!
  ) {
    result: setRegisteredProductCustomField(input: $input) {
      code
      success
      message
      registeredProduct(locale: de) {
        productId
        registrationId
        productNumber: customFieldText(key: "productNumber")
      }
    }
    einreichResult: setRegisteredProductCustomField(input: $einreichInput) {
      code
      success
      message
      registeredProduct(locale: de) {
        productId
        registrationId
        einreichnummer: customFieldText(key: "einreichnummer")
      }
    }
  }
`;

/**
 *
 * @param {Parameters<LKBurgenlandEinreichung_beforeRender>[0]["client"]} client
 * @param {ReturnType<afterQuery>["renderedRegistrations"][number]} registration
 * @param {number} newProductNumber
 * @returns
 */
async function setLastProductNumber(
  client,
  { registrationId },
  newProductNumber
) {
  const text = `${newProductNumber}`;
  const {
    data: { result },
    // errors,
  } = await client.mutate({
    mutation: setRegistrationNumberMutation,
    variables: {
      input: {
        registrationId,
        key: 'productNumber',
        value: { text },
      },
    },
  });
  console.log(result);
  if (!result.success) throw new Error(result.message);
  return text;
  // TODO return the actual result
  // return result.registration?.registrationNumber;
}
